import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Icon from '../../../../shared/_TBComponents/Icon';
import Text from '../../../../shared/_TBComponents/Text';
import ProgressBar from './ProgressBar';

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 20px;
  background: ${(props) => props.theme.colors.surface};
  border: 1px solid ${(props) => props.theme.colors.borderLight};
  border-radius: 4px;
`;

const TopRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
`;

const GoalTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ProgressRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 2px;
  max-width: 420px;
  font-size: 14px;
  color: ${(props) => props.theme.colors.text};
`;

const LinkButton = styled.button`
  flex-shrink: 0;
  padding: 0;
  border: none;
  background: none;
  font-family: "Nunito Sans";
  font-size: 14px;
  font-weight: 700;
  color: ${(props) => props.theme.colors.text};
  cursor: pointer;

  &:hover {
    color: ${(props) => props.theme.colors.hover};
  }
`;

const CardLabel = ({ sourceLabel }) => (
  <Text
    upperCase
    styledTextProps={{
      fontSize: '11px',
      fontWeight: 700,
      letterSpacing: '0.04em',
      color: sourceLabel ? '#5B86E5' : '#9E9E9E',
    }}
  >
    {sourceLabel ? `From ${sourceLabel}` : 'Goal'}
  </Text>
);

CardLabel.propTypes = { sourceLabel: PropTypes.string };
CardLabel.defaultProps = { sourceLabel: null };

// Shows the goal as in progress/achieved/not set
const HistoryGoalBlock = ({
  goalValue, goalDetail, sourceLabel, goalPct, goalColor,
  achievedGoal, achievedOn, achievedWith, onSetGoal,
}) => {
  if (achievedGoal) {
    return (
      <Card>
        <CardLabel sourceLabel={sourceLabel} />
        <TopRow>
          <GoalTitle>
            <Icon icon='trophy' size={16} color='#E8A33A' />
            <Text styledTextProps={{ fontSize: '18px', fontWeight: 700 }}>{`Goal: ${achievedGoal}`}</Text>
          </GoalTitle>
          {onSetGoal && <LinkButton type='button' onClick={onSetGoal}>Set next goal</LinkButton>}
        </TopRow>
        <Text styledTextProps={{ fontSize: '13px', color: '#9E9E9E' }}>
          {`Hit ${achievedOn}${achievedWith ? ` with ${achievedWith}` : ''}`}
        </Text>
      </Card>
    );
  }

  if (!goalValue) {
    return (
      <Card>
        <CardLabel />
        <TopRow>
          <Text styledTextProps={{ fontSize: '18px', fontWeight: 700 }}>No goal</Text>
          {onSetGoal && <LinkButton type='button' onClick={onSetGoal}>Set a goal</LinkButton>}
        </TopRow>
        {onSetGoal && (
          <Text styledTextProps={{ fontSize: '13px', color: '#9E9E9E' }}>Track progress toward one</Text>
        )}
      </Card>
    );
  }

  return (
    <Card>
      <CardLabel sourceLabel={sourceLabel} />
      <TopRow>
        <Text styledTextProps={{ fontSize: '18px', fontWeight: 700 }}>
          {`Goal: ${goalValue}`}
          {goalDetail && ` (${goalDetail})`}
        </Text>
        {onSetGoal && <LinkButton type='button' onClick={onSetGoal}>Change goal</LinkButton>}
      </TopRow>
      {goalPct !== null ? (
        <>
          <Text styledTextProps={{ fontSize: '13px', color: '#9E9E9E' }} styledWrapperProps={{ marginBottom: '-6px' }}>
            Progress
          </Text>
          <ProgressRow>
            <ProgressBar percent={goalPct} color={goalColor} height='8px' />
            <span>{`${goalPct}%`}</span>
          </ProgressRow>
        </>
      ) : (
        <Text styledTextProps={{ fontSize: '13px', color: '#9E9E9E' }}>Not started</Text>
      )}
    </Card>
  );
};

HistoryGoalBlock.propTypes = {
  goalValue: PropTypes.string,
  goalDetail: PropTypes.string,
  sourceLabel: PropTypes.string,
  goalPct: PropTypes.number,
  goalColor: PropTypes.string,
  achievedGoal: PropTypes.string,
  achievedOn: PropTypes.string,
  achievedWith: PropTypes.string,
  onSetGoal: PropTypes.func,
};

HistoryGoalBlock.defaultProps = {
  onSetGoal: null,
  goalValue: null,
  goalDetail: null,
  sourceLabel: null,
  goalPct: null,
  goalColor: null,
  achievedGoal: null,
  achievedOn: null,
  achievedWith: null,
};

export default HistoryGoalBlock;
